import React, { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { usePublicCmsPage } from 'features/cms/hooks/usePublicCms';
import { useLanguage } from 'contexts/LanguageContext';
import { Loader2, Globe, ArrowLeft } from 'lucide-react';
import { sanitizeHtmlContent } from 'utils/sanitizeHtml';
import 'styles/cms-content.css';

const PublicCmsPage = ({ defaultSlug }) => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Map routes to slugs
  const routeToSlugMap = {
    '/terms-conditions': 'terms-and-conditions-customer',
    '/terms-and-conditions-customer': 'terms-and-conditions-customer',
    '/terms-and-conditions-provider': 'terms-and-conditions-provider',
    '/terms-conditions-customer': 'terms-and-conditions-customer',
    '/terms-conditions-provider': 'terms-and-conditions-provider',
    '/privacy-policy': 'privacy-policy',
    '/about-us': 'about-qkly',
    '/account-deletion': 'account-deletion',
  };

  // Determine slug: from params, defaultSlug prop, or route mapping
  const slug = paramSlug || defaultSlug || routeToSlugMap[location.pathname];
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const { data, isLoading, isError, error } = usePublicCmsPage(slug);

  // Get content in the current language, fallback to English
  const getLocalizedContent = (contentObj) => {
    if (!contentObj) return '';
    return contentObj[currentLanguage] || contentObj.english || '';
  };

  const getLocalizedTitle = (titleObj) => {
    if (!titleObj) return '';
    return titleObj[currentLanguage] || titleObj.english || '';
  };

  // Extract data and sanitize BEFORE any conditional returns (Rules of Hooks)
  const pageData = data?.data;
  const title = getLocalizedTitle(pageData?.title);
  const rawContent = getLocalizedContent(pageData?.content);
  const shortDescription = getLocalizedContent(pageData?.shortDescription);

  // Sanitize HTML content to remove problematic inline styles
  const content = useMemo(() => sanitizeHtmlContent(rawContent), [rawContent]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    console.error('CMS Page Error:', error);
    console.error('Attempted slug:', slug);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-red-600 mb-2">Something went wrong</h2>
            <p className="text-red-800 mb-2">
              {error?.response?.data?.message || error?.message || 'We encountered an unexpected error while processing your request.'}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Slug: {slug}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header with Language Switcher */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-end">
            {/* Language Switcher */}
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-500" />
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {Object.entries(languages).map(([code, lang]) => (
                  <button
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${currentLanguage === code
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                      }`}
                    title={lang.name}
                  >
                    {lang.nativeName}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <article className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Article Header */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 sm:px-8 lg:px-12 py-8 sm:py-12 border-b border-gray-200">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {title || 'Untitled'}
            </h1>
            {shortDescription && (
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl">
                {shortDescription}
              </p>
            )}
          </div>

          {/* Article Body */}
          <div className="px-6 sm:px-8 lg:px-12 py-8 sm:py-12">
            {content ? (
              <div
                className="cms-content prose prose-lg max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-gray-900
                  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-gray-900
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4 prose-ul:text-gray-700
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4 prose-ol:text-gray-700
                  prose-li:text-gray-700 prose-li:mb-2
                  prose-blockquote:border-l-4 prose-blockquote:border-blue-600
                  prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600
                  prose-code:text-blue-600 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                  prose-pre:bg-gray-900 prose-pre:text-gray-100
                  prose-img:rounded-lg prose-img:shadow-md
                  [&_h2]:!text-gray-900 [&_h2]:!font-bold [&_h2]:!text-2xl [&_h2]:!mt-8 [&_h2]:!mb-4
                  [&_h3]:!text-gray-900 [&_h3]:!font-bold [&_h3]:!text-xl [&_h3]:!mt-6 [&_h3]:!mb-3
                  [&_p]:!text-gray-700 [&_p]:!leading-relaxed [&_p]:!mb-4
                  [&_ul]:!list-disc [&_ul]:!pl-6 [&_ul]:!mb-4
                  [&_ol]:!list-decimal [&_ol]:!pl-6 [&_ol]:!mb-4
                  [&_li]:!text-gray-700 [&_li]:!mb-2
                  [&_strong]:!text-gray-900 [&_strong]:!font-semibold
                  [&_a]:!text-blue-600 [&_a]:!no-underline hover:[&_a]:!underline"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No content available in {languages[currentLanguage].name}.
                </p>
                {currentLanguage !== 'english' && (
                  <button
                    onClick={() => changeLanguage('english')}
                    className="mt-4 text-primary hover:underline"
                  >
                    Switch to English
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer Metadata */}
          <div className="bg-gray-50 px-6 sm:px-8 lg:px-12 py-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              {pageData?.createdAt && (
                <div>
                  <span className="font-medium">Published:</span>{' '}
                  {new Date(pageData.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              )}
              {pageData?.updatedAt && pageData.updatedAt !== pageData.createdAt && (
                <div>
                  <span className="font-medium">Last Updated:</span>{' '}
                  {new Date(pageData.updatedAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              )}
            </div>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Qkly. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PublicCmsPage;
